import { Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent } from '@angular/material/dialog';
import { PrepLocationsService } from 'src/app/services/prep-locations.service';
import { IPrepLocation } from 'src/app/common/models/prep-location';
import { ToastrService } from 'ngx-toastr';
import { MatRadioChange, MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { IMultiStoreSelect } from 'src/app/common/models/multiStoreSelect';
import { ItemsService } from 'src/app/services/items.service';
import { catchError, forkJoin, map, Observable, of, tap } from 'rxjs';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { UnimportedItemGroup } from 'src/app/common/models/unimportedItemGroup';
import { OrderableItemPrepLocation } from 'src/app/common/models/orderableItemPrepLocation';
import { StatusService } from 'src/app/services/status.service';
import { LoggingService } from 'src/app/services/logging.service';
import { AuthManager } from 'src/app/core/auth/auth.manager';
import { CdkScrollable } from '@angular/cdk/scrolling';
import {
  MatStepper,
  MatStep,
  MatStepLabel,
  MatStepperNext,
  MatStepContent,
  MatStepperPrevious,
} from '@angular/material/stepper';
import { NgClass } from '@angular/common';
import { MatLabel, MatFormField, MatInput, MatHint, MatSuffix } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { IncrementInputComponent } from '../../../../modules/shared/components/increment-input/increment-input.component';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { MultiStoreDropdown } from '../../multi-store-dropdown/multi-store-dropdown.component';

@Component({
  templateUrl: './import-item.component.html',
  styleUrls: ['./import-item.component.scss'],
  standalone: true,
  imports: [
    CdkScrollable,
    MatDialogContent,
    MatStepper,
    MatStep,
    FormsModule,
    ReactiveFormsModule,
    MatStepLabel,
    NgClass,
    MatLabel,
    MatFormField,
    MatSelect,
    MatOption,
    IncrementInputComponent,
    MatRadioGroup,
    MatRadioButton,
    MatInput,
    MatDatepickerInput,
    MatHint,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatCheckbox,
    MatButton,
    MatStepperNext,
    MatStepContent,
    MultiStoreDropdown,
    MatStepperPrevious,
  ],
})
export class ItemModalComponent {
  public authManager: AuthManager;
  public firstFormGroup: UntypedFormGroup;
  public secondFormGroup: UntypedFormGroup;
  public handoffTypesForm: UntypedFormGroup = this._formBuilder.group({
    carryout: true,
    curbside: true,
    delivery: true,
    kiosk: true,
    tableside: true,
  });
  public pickupDatesForm: UntypedFormGroup = this._formBuilder.group({
    startDate: ['', Validators.required],
    endDate: ['', Validators.required],
  });

  public limitedQuantityForm: UntypedFormGroup = this._formBuilder.group({
    sellableQuantity: [0, [Validators.required, Validators.min(0)]],
  });

  private _isClickable = true;
  public get isClickable(): boolean {
    return (
      this._isClickable
      && this.getSelectedHandoffTypes().length > 0
      && (!this.isDateRestricted || this.pickupDatesForm.valid)
    );
  }

  public isMultiStore = false;
  public isDateRestricted = false;
  public isSellableQuantityEnabled = false;
  public currentFacilityId: string;
  public prepLocations: IPrepLocation[] = [];
  public facilities: IMultiStoreSelect[] = [];
  public leadTime = 0;
  public prepTime = 0;
  public postPrepTime = 0;
  public selectedPrepLocation = 0;

  constructor(
    public dialogRef: MatDialogRef<ItemModalComponent>,
    private auth: AuthManager,
    private itemService: ItemsService,
    private _facilityManager: FacilityManager,
    private prepLocationService: PrepLocationsService,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: UnimportedItemGroup,
    private _formBuilder: UntypedFormBuilder,
    private _statusService: StatusService,
    private _loggingService: LoggingService,
  ) {
    this.authManager = this.auth;
    this.currentFacilityId = this._facilityManager.getCurrentFacility();

    this.prepLocationService.getPrepLocations(this.currentFacilityId).subscribe({
      next: (d) => {
        this.prepLocations = d;
      },
      error: (e) => {
        this.toastr.error('Failed to load Prep Locations');
      },
    });

    this.firstFormGroup = this._formBuilder.group({
      prepLocation: ['', Validators.required],
    });

    this.secondFormGroup = this._formBuilder.group({
      facilities: ['', Validators.required],
    });

    this.togglePickupDatesForm(this.isDateRestricted);
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  private calculateLeadMinutesFromLeadHours(): number {
    return this.leadTime * 60;
  }

  public prepDepartmentChange(): void {
    const selectedPrepLocation = this.prepLocations.find((pl) => {
      return pl.prepLocationTypeName === this.firstFormGroup.get('prepLocation')!.value;
    });
    this.prepTime = selectedPrepLocation?.defaultPrepMinutes || this.prepTime;
    this._isClickable = true;
  }

  public importItemToFacilities(): void {
    this._isClickable = false;
    if (this.firstFormGroup.status === 'INVALID') {
      this.toastr.warning('Information missing from Item Import');
      return;
    }

    let selectedFacilityIds: string[] = [];
    if (this.isMultiStore) {
      selectedFacilityIds = this.facilities.filter((x) => x.isSelected).map((x) => x.facilityId);
    } else {
      selectedFacilityIds.push(this._facilityManager.getCurrentFacility());
    }

    const importObserables: Observable<object>[] = [];

    this.data.unimportedItems.forEach((uii) => {
      selectedFacilityIds.forEach((fId) => {
        const itemToImport: OrderableItemPrepLocation = {
          organizationId: 1,
          facilityId: fId,
          prepLocationTypeName: this.firstFormGroup.get('prepLocation')!.value,
          commerceItemNumber: uii.commerceItemNumber,
          prepMinutes: this.prepTime,
          postPrepMinutes: this.postPrepTime,
          leadMinutes: this.calculateLeadMinutesFromLeadHours(),
          startPickupDate: this.isDateRestricted ? this.pickupDatesForm.value.startDate : undefined,
          endPickupDate: this.isDateRestricted ? this.pickupDatesForm.value.endDate : undefined,
          allowedHandoffTypes: this.getSelectedHandoffTypes(),
          sellableQuantity:
            this.isDateRestricted && this.isSellableQuantityEnabled
              ? this.limitedQuantityForm.value.sellableQuantity
              : 0,
          isSellableQuantityEnabled: this.isSellableQuantityEnabled,
        };

        importObserables.push(
          this.itemService
            .importOrderableItemPrepLocation(itemToImport)
            .pipe(
              map((success) => ({ isError: false, error: null, facilityId: itemToImport.facilityId })),
              catchError((err) => {
                const errObj = of({ isError: true, error: err, facilityId: itemToImport.facilityId });
                this._loggingService.logTrace('import orderable item prep location error', errObj);
                return of(errObj);
              }),
            )
            .pipe(
              tap(() => {
                this._statusService.incrementCompletedRequests();
              }),
            ),
        );
      });
    });

    this._statusService.batchProgressSetup(importObserables.length);

    forkJoin(importObserables).subscribe({
      next: (response) => {
        this.toastr.success('Item Imported!');
        this.dialogRef.close(this.data);
      },
      error: (err) => {
        this.toastr.error('Error while importing item: ' + err);
        this._isClickable = true;
      },
    });
  }

  public multiStoreButton(event: MatRadioChange): void {
    this.isMultiStore = event.value;
  }

  public pickupRestrictionButton(event: MatRadioChange): void {
    this.isDateRestricted = event.value;
    this.togglePickupDatesForm(this.isDateRestricted);
  }

  public limitedQuantityButton(event: MatRadioChange): void {
    this.isSellableQuantityEnabled = event.value;
  }

  public togglePickupDatesForm(enabled: boolean): void {
    if (enabled) {
      this.pickupDatesForm.controls['startDate'].enable();
      this.pickupDatesForm.controls['endDate'].enable();
      this.pickupDatesForm.markAllAsTouched();
    } else {
      this.pickupDatesForm.controls['startDate'].disable();
      this.pickupDatesForm.controls['endDate'].disable();
    }
  }

  public toggleLimitedQuantityForm(enabled: boolean): void {
    if (enabled) {
      this.limitedQuantityForm.controls['sellableQuantity'].enable();
      this.limitedQuantityForm.markAllAsTouched();
    } else {
      this.limitedQuantityForm.controls['sellableQuantity'].disable();
    }
  }

  public getSelectedHandoffTypes(): string[] {
    const selected = [];
    if (this.handoffTypesForm.value.carryout) {
      selected.push('store');
    }
    if (this.handoffTypesForm.value.curbside) {
      selected.push('curbside');
    }
    if (this.handoffTypesForm.value.delivery) {
      selected.push('delivery');
    }
    if (this.handoffTypesForm.value.kiosk) {
      selected.push('kiosk');
    }
    if (this.handoffTypesForm.value.tableside) {
      selected.push('tableside');
    }
    return selected;
  }
}
